import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import KpiCard from "../components/KpiCard";
import StatusBadge from "../components/StatusBadge";
import LiveEmptyState from "../components/LiveEmptyState";
import useLiveMode from "../hooks/useLiveMode";
import api from "../lib/api";
import { formatINR, formatNum, cplHealth, formatShortDate } from "../utils/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";
import { Wallet, Users, Target, AlertTriangle, Activity, Sparkles, CheckSquare } from "lucide-react";
import { useGlobalDate } from "../contexts/GlobalDateContext";

// Convert a global date range key into the integer "days" param the
// /dashboard/trends endpoint accepts (clamped to the 7-90 backend bounds).
function rangeToDays(range, from, to) {
  if (!from || !to) return 30;
  const d = (new Date(to) - new Date(from)) / 86400000 + 1;
  return Math.max(7, Math.min(90, Math.round(d)));
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState([]);
  const [topCamps, setTopCamps] = useState([]);
  const [clientPerf, setClientPerf] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusDist, setStatusDist] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const { isLive, isConnected } = useLiveMode();
  const { range, from, to, label } = useGlobalDate();

  useEffect(() => {
    const days = rangeToDays(range, from, to);
    Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/trends", { params: { days } }),
      api.get("/dashboard/top-campaigns"),
      api.get("/dashboard/client-performance"),
      api.get("/ai/daily-summary"),
      api.get("/campaigns/status/distribution"),
      api.get("/alerts", { params: { status: "new" } }),
    ]).then(([k, t, tc, cp, s, sd, al]) => {
      setKpis(k.data); setTrends(t.data); setTopCamps(tc.data);
      setClientPerf(cp.data); setSummary(s.data); setStatusDist(sd.data);
      setAlerts(al.data);
    }).catch(console.error);
  }, [range, from, to]);

  const cplTone = kpis ? cplHealth(kpis.avg_cpl_today) : "muted";
  const noLiveData =
    isLive && kpis &&
    (kpis.active_campaigns || 0) +
    (kpis.paused_campaigns || 0) +
    (kpis.limited_campaigns || 0) +
    (kpis.learning_campaigns || 0) +
    (kpis.ended_campaigns || 0) === 0;

  return (
    <Layout title="Command Center" subtitle="Real-time agency overview">
      {noLiveData ? (
        isConnected ? (
          <LiveEmptyState
            entity="data"
            variant="module"
            title="Live sync in progress"
            message="Your data source is connected. Once the first sync completes, real-time KPIs will populate this page automatically. Use Sync Now in Admin → Connectors to trigger immediately."
            testId="dashboard-live-empty"
          />
        ) : (
          <LiveEmptyState
            entity="data"
            title="No live data synced yet"
            message="You're viewing LIVE mode — demo data is hidden. Connect Google Ads to fetch campaigns & metrics, connect Google Sheets for lead pipelines, or upload a CSV to see your Command Center come alive."
            ctaHref="/admin?tab=connectors"
            ctaLabel="Open connectors"
            testId="dashboard-live-empty"
          />
        )
      ) : (
      <>
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          testId="kpi-spend-today"
          label="Spend · Today"
          value={formatINR(kpis?.total_spend_today ?? 0, { compact: true })}
          tone="primary"
          sub={`₹${Number(kpis?.total_spend_month ?? 0).toLocaleString("en-IN")} MTD`}
          icon={Wallet}
          to="/budget"
        />
        <KpiCard
          testId="kpi-leads-today"
          label="Leads · Today"
          value={formatNum(kpis?.total_leads_today ?? 0)}
          tone="success"
          sub={`${formatNum(kpis?.total_leads_month ?? 0)} this month`}
          icon={Users}
          to="/leads"
        />
        <KpiCard
          testId="kpi-cpl-today"
          label="Avg CPL · Today"
          value={formatINR(kpis?.avg_cpl_today ?? 0)}
          tone={cplTone}
          sub={`₹${Math.round(kpis?.avg_cpl_month ?? 0)} MTD avg`}
          icon={Target}
          to="/campaigns"
        />
        <KpiCard
          testId="kpi-critical-alerts"
          label="Critical Alerts"
          value={formatNum(kpis?.critical_alerts ?? 0)}
          tone={kpis?.critical_alerts ? "critical" : "success"}
          sub={`${kpis?.warning_alerts ?? 0} warnings · ${kpis?.accounts_requiring_attention ?? 0} accounts need attention`}
          icon={AlertTriangle}
          to="/alerts?severity=critical"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Active Campaigns" value={kpis?.active_campaigns ?? 0} tone="success" sub={`${kpis?.paused_campaigns ?? 0} paused`} icon={Activity} testId="kpi-active-campaigns" to="/campaigns?status=active" />
        <KpiCard label="Limited / Learning" value={(kpis?.limited_campaigns ?? 0) + (kpis?.learning_campaigns ?? 0)} tone="warning" sub="Needs review" icon={Activity} testId="kpi-limited-learning" to="/campaigns?status=limited" />
        <KpiCard label="Pending Tasks" value={kpis?.pending_tasks ?? 0} tone="primary" sub="Team actions open" icon={CheckSquare} testId="kpi-pending-tasks" to="/tasks?status=open" />
        <KpiCard label="AI Priority" value={summary?.priorities?.length || 0} tone="ai" sub={summary?.headline || "—"} icon={Sparkles} testId="kpi-ai-priority" to="/ai-insights" />
      </div>

      {/* Chart + AI Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <div className="lg:col-span-3 bg-card border border-border rounded-sm p-5" data-testid="spend-vs-leads-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono">Performance · Last 30 days</div>
              <div className="font-heading text-lg mt-1 tracking-tight">Spend vs. Leads</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[hsl(var(--primary))]" />Spend (₹)</div>
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[hsl(var(--success))]" />Leads</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trends} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(220 15% 90%)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} tickFormatter={(d) => formatShortDate(d)} />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
              <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(220 15% 90%)", fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="spend" stroke="hsl(214 100% 35%)" strokeWidth={2} dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="leads" stroke="hsl(150 70% 30%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border border-t-2 border-t-[hsl(var(--ai))] rounded-sm p-5" data-testid="ai-summary-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" strokeWidth={2} />
            <div className="label-mono text-[hsl(var(--ai))]">AI Daily Summary</div>
          </div>
          <div className="font-heading text-base font-medium leading-snug mb-4">
            {summary?.headline || "Analyzing…"}
          </div>
          <div className="space-y-2.5">
            {summary?.priorities?.map((p, i) => (
              <div key={i} className="border-l-2 pl-3 py-0.5" style={{
                borderLeftColor: p.severity === "critical" ? "hsl(var(--critical))"
                  : p.severity === "warning" ? "hsl(var(--warning))" : "hsl(var(--primary))"
              }}>
                <div className="text-sm font-medium leading-tight">{p.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.detail}</div>
              </div>
            ))}
          </div>
          <Link to="/ai-insights" className="inline-flex items-center gap-1 text-xs mt-4 text-[hsl(var(--ai))] hover:underline font-medium">
            View AI Insights →
          </Link>
        </div>
      </div>

      {/* Alerts + Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card border border-border rounded-sm p-5" data-testid="alerts-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono">Live</div>
              <div className="font-heading text-lg mt-1 tracking-tight">Active Alerts</div>
            </div>
            <Link to="/alerts" className="text-xs text-primary hover:underline font-medium">View all →</Link>
          </div>
          <div className="space-y-0">
            {alerts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-start gap-3 py-2.5 border-b border-border last:border-b-0">
                <StatusBadge tone={a.severity === "critical" ? "critical" : a.severity === "warning" ? "warning" : "primary"}>
                  {a.severity}
                </StatusBadge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{a.campaign_name || a.client_name}</div>
                </div>
              </div>
            ))}
            {alerts.length === 0 && <div className="text-sm text-muted-foreground py-4">No active alerts.</div>}
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-5" data-testid="campaign-status-chart">
          <div className="mb-4">
            <div className="label-mono">Inventory</div>
            <div className="font-heading text-lg mt-1 tracking-tight">Campaign Status Distribution</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusDist} layout="vertical" margin={{ top: 0, right: 20, left: 40, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(220 15% 90%)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
              <YAxis type="category" dataKey="status" tick={{ fontSize: 11, fill: "hsl(220 15% 40%)" }} width={70} />
              <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(220 15% 90%)", fontSize: 12 }} />
              <Bar dataKey="count" fill="hsl(214 100% 35%)" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Client Performance + Top Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-sm p-5" data-testid="client-performance-chart">
          <div className="mb-4">
            <div className="label-mono">Last 7 days</div>
            <div className="font-heading text-lg mt-1 tracking-tight">Client Performance</div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={clientPerf} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(220 15% 90%)" vertical={false} />
              <XAxis dataKey="client_name" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
              <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(220 15% 90%)", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="leads" fill="hsl(150 70% 30%)" name="Leads" radius={[2, 2, 0, 0]} />
              <Bar dataKey="cpl" fill="hsl(214 100% 35%)" name="CPL ₹" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-sm p-5" data-testid="top-campaigns-list">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono">Last 7 days</div>
              <div className="font-heading text-lg mt-1 tracking-tight">Top Campaigns by Leads</div>
            </div>
            <Link to="/campaigns" className="text-xs text-primary hover:underline font-medium">All campaigns →</Link>
          </div>
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th className="text-right">Spend</th>
                <th className="text-right">Leads</th>
                <th className="text-right">CPL</th>
              </tr>
            </thead>
            <tbody>
              {topCamps.slice(0, 6).map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/campaigns/${c.id}`} className="text-sm hover:text-primary font-medium">
                      {c.name}
                    </Link>
                  </td>
                  <td className="text-right">{formatINR(c.spend_7d, { compact: true })}</td>
                  <td className="text-right font-medium">{c.leads_7d}</td>
                  <td className="text-right">
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-sm ${
                      cplHealth(c.cpl_7d) === "success" ? "text-[hsl(var(--success))]" :
                      cplHealth(c.cpl_7d) === "warning" ? "text-[hsl(38_92%_30%)]" : "text-[hsl(var(--critical))]"
                    }`}>
                      {c.cpl_7d ? formatINR(c.cpl_7d) : "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </Layout>
  );
}
